import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

import { BrandMark } from "@/components/BrandMark";
import { OrderSummaryView } from "@/components/orders/OrderSummaryView";
import { SignFlow } from "@/components/orders/SignFlow";
import { BTN_GHOST } from "@/components/orders/ui";
import { api } from "@/lib/api-client";
import type { OrderFull, PublicOrderView } from "@/lib/orders";

/**
 * `/orders/:id/review` — the salesperson hands the device to the customer:
 * a clean visual summary first; when the customer has checked it, staff
 * presses "Send code" and the customer signs on the same screen.
 */
export default function OrderReviewPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderFull | null>(null);
  const [view, setView] = useState<PublicOrderView | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const o = await api.order(id);
    setOrder(o);
    setView(await api.publicOrder(o.publicToken));
  };

  useEffect(() => {
    load().catch(() => setError("Order not found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) return <div className="app-surface min-h-screen p-6 text-sm text-pink">{error}</div>;
  if (!order || !view) return <div className="app-surface min-h-screen p-6 text-sm text-ice/60">Loading…</div>;

  const pdfUrl = api.orderPdfUrl(order.id);

  return (
    <div className="app-surface min-h-screen px-4 py-6">
      <div className="mx-auto max-w-xl">
        <div className="mb-5 flex items-center justify-between">
          <Link to={`/orders/${order.id}`} className="inline-flex items-center gap-2 text-xs text-ice/70 hover:text-paper">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to order
          </Link>
          <div className="flex items-center gap-2">
            <BrandMark className="h-8 w-8" />
            <span className="brand-wordmark text-[0.6rem] text-paper">VERSUS</span>
          </div>
        </div>

        <div className="brand-kicker">Your order · {view.number}</div>
        <h1 className="font-serif text-3xl text-paper">{view.customerName}</h1>
        <p className="mb-5 text-sm text-ice/70">{view.siteAddress}</p>

        {signedUrl || order.status === "signed" ? (
          <div className="glass-panel-strong p-6 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green" />
            <p className="mt-3 font-serif text-2xl text-paper">Signed. Thank you!</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className={BTN_GHOST}>Open signed PDF</a>
              <button type="button" className={BTN_GHOST} onClick={() => navigate(`/orders/${order.id}`)}>Back to order</button>
            </div>
          </div>
        ) : order.status === "cancelled" ? (
          <div className="glass-panel-strong p-5 text-sm text-paper/90">This order is cancelled.</div>
        ) : (
          <>
            <OrderSummaryView view={view} pdfUrl={pdfUrl} />
            <div className="mt-6 border-t border-white/10 pt-6">
              <p className="mb-3 text-center text-xs uppercase tracking-[0.2em] text-ice/50">When the customer has checked everything</p>
              {order.requiresApproval && !order.approvedAt ? (
                <div className="glass-panel-strong p-4 text-sm text-goldbright">The discount on this order needs an admin's approval before it can be signed.</div>
              ) : (
                <SignFlow
                  defaultName={view.customerName}
                  hasEmail={view.hasEmail}
                  startLabel="Send the code to the customer's WhatsApp"
                  requestOtp={async (channel) => {
                    const r = await api.orderOtp(order.id, channel);
                    await load();
                    return r;
                  }}
                  sign={(dto) => api.signOrder(order.id, dto)}
                  onSigned={(url) => {
                    setSignedUrl(url);
                    void load();
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
